import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { useGetVoicesSeats } from '@/components/Modals/CloneVoiceWizardModal/useGetVoicesSeats';
import { toast } from '@/components/toast';
import { MULTILINGUAL } from '@/constants';
import CloneVoice from '@/images/CloneVoice.svg';
import Microphone from '@/images/microphone3.svg';
import Plus from '@/images/plus.svg';
import Settings from '@/images/Settings.svg';
import { useAuth } from '@/services/auth';
import {
    cloneVoiceWizardModal as _cloneVoiceWizardModal,
    cloneVoiceUpgradePlanModal,
    ttsSignupModal,
    ttsUpgradePlanModal,
    ttsVoiceModal,
    ttsVoiceSourceFilter,
} from '@/state/presenters';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { PlanGroup } from '@d-id/types';
import styled from '@emotion/styled';
import { Tab, Tabs } from '@mui/material';
import mixpanel from 'mixpanel-browser';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

export const supported11labsVoices: { language: string; locale: string }[] = [
    { language: 'Chinese', locale: 'zh-CN' },
    { language: 'Korean (Korea)', locale: 'ko-KR' },
    { language: 'Dutch (Netherlands)', locale: 'nl-NL' },
    { language: 'Turkish (Turkey)', locale: 'tr-TR' },
    { language: 'Swedish (Sweden)', locale: 'sv-SE' },
    { language: 'Indonesian (Indonesia)', locale: 'id-ID' },
    { language: 'Filipino (Philippines)', locale: 'fil-PH' },
    { language: 'Japanese (Japan)', locale: 'ja-JP' },
    { language: 'Ukrainian (Ukraine)', locale: 'uk-UA' },
    { language: 'Greek (Greece)', locale: 'el-GR' },
    { language: 'Czech (Czechia)', locale: 'cs-CZ' },
    { language: 'Finnish (Finland)', locale: 'fi-FI' },
    { language: 'Romanian (Romania)', locale: 'ro-RO' },
    { language: 'Russian (Russia)', locale: 'ru-RU' },
    { language: 'Danish (Denmark)', locale: 'da-DK' },
    { language: 'Bulgarian (Bulgaria)', locale: 'bg-BG' },
    { language: 'Malay (Malaysia)', locale: 'ms-MY' },
    { language: 'Slovak (Slovakia)', locale: 'sk-SK' },
    { language: 'Croatian (Croatia)', locale: 'hr-HR' },
    { language: 'Tamil', locale: 'ta-IN' },
    { language: 'English (United States)', locale: 'en-US' },
    { language: 'Polish (Poland)', locale: 'pl-PL' },
    { language: 'German (Germany)', locale: 'de-DE' },
    { language: 'Spanish (Spain)', locale: 'es-ES' },
    { language: 'French (France)', locale: 'fr-FR' },
    { language: 'Italian (Italy)', locale: 'it-IT' },
    { language: 'Hindi (India)', locale: 'hi-IN' },
    { language: 'Portuguese (Portugal)', locale: 'pt-PT' },
    { language: 'English', locale: 'en-US' },
    { language: 'Arabic', locale: 'ar' },
];

interface Props {
    labelOff?: boolean;
    language?: string;
}

const StyledList = styled.ul`
    min-height: 40vh;
    list-style: none;
    margin: 0;
    padding: 8px 0;
    max-height: 40vh;
    position: relative;
`;

const StyledTabs = styled(Tabs)`
    margin: 0 12px;
    border-bottom: 1px solid #7c7c7a30;

    & .MuiButtonBase-root {
        font-size: 14px;
        font-family: Manrope !important;
        color: rgba(0, 0, 0, 0.6);
        padding: 8px;
        text-transform: none;
    }

    & .MuiButtonBase-root.Mui-selected {
        color: ${theme.colors.secondary.main};
    }
`;

const MultiSelectFooter = styled.div`
    position: absolute;
    bottom: 0;
    height: 62px;
    border-top: 1px solid #7c7c7a30;
    width: 100%;
    display: flex;
    background: ${({ theme }) => theme.colors.white};
`;

const TopOption = styled.div`
    height: 48px;
    border-top: 1px solid #7c7c7a30;
    width: 100%;
    display: flex;
    background: ${({ theme }) => theme.colors.black[50]};
`;

const OptionWrapper = styled.div<{ isFooter: boolean }>`
    height: ${({ isFooter }) => `calc(40vh - 57px - ${isFooter ? '62px' : '0px'})`};
    overflow: auto;
`;

const StyledIconButton = styled(IconButton)`
    width: 100%;
    justify-content: start;
    padding: 0 24px;
    border-radius: 0;
    font-size: 16px;
    color: ${theme.colors.secondary.main} !important;
    font-weight: 500;

    svg {
        margin-right: 8px;
    }
`;

const NotSupportedVoiceContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-width: 60%;
    margin: 44px auto 0;
    font-size: 14px;
    text-align: center;
    color: ${({ theme }) => theme.colors.secondary[500]};

    img {
        margin-bottom: 8px;
    }
`;

const StyledNew = styled('span')`
    color: #29cc6a;
    background: #e7ffe7;
    border-radius: 0.4rem;
    box-shadow: 0px 0px 12.444px 0px rgba(0, 0, 0, 0.1);
    text-align: center;
    font-size: 14px;
    margin-left: 8px;
    display: flex;
    padding: 2px 3px;
    justify-content: center;
    align-items: center;
    gap: 9px;
`;

const UNSUPPORTED_PLAN_CLONE_VOICE = [PlanGroup.LITE, PlanGroup.TRIAL, PlanGroup.FREE];
const isSupportedLanguage = (selectedLanguage: string | null): boolean => {
    return MULTILINGUAL === selectedLanguage || supported11labsVoices.some(({ language }) => language === selectedLanguage);
};

interface Props {
    children?: React.ReactNode;
}

export const ListboxComponent = observer(
    React.forwardRef<HTMLUListElement>((rest: Props, ref) => {
        const agentEditorStore = useAgentEditorStore();
        const { user, isAnonymous } = useAuth();
        const setShowVoiceCloneUpgradeModal = useSetRecoilState(cloneVoiceUpgradePlanModal);
        const setSignupModalState = useSetRecoilState(ttsSignupModal);
        const ttsVoiceSourceFilterValue = useRecoilValue(ttsVoiceSourceFilter);
        const filter = isAnonymous ? 'default' : ttsVoiceSourceFilterValue;

        const [ttsFilterValue, setTtsFilter] = useRecoilState(ttsVoiceSourceFilter);
        const [_voiceModalState, setVoiceModalState] = useRecoilState(ttsVoiceModal);
        const [_upgradePlanModalState, setUpgradePlanModalState] = useRecoilState(ttsUpgradePlanModal);
        const setCloneVoiceWizardModal = useSetRecoilState(_cloneVoiceWizardModal);
        const { data: seats } = useGetVoicesSeats();

        const [value, setValue] = React.useState<'default' | 'imported'>((!isAnonymous && ttsFilterValue) || 'default');
        const handleChange = (_event: React.SyntheticEvent, newValue: 'default' | 'imported') => {
            setValue(newValue);
            setTtsFilter(newValue);
        };

        const handleModalOpen = () => {
            if (
                user?.planGroup &&
                [PlanGroup.PRO, PlanGroup.ADVANCED, PlanGroup.ENTERPRISE, PlanGroup.ENTERPRISE_TRIAL].includes(user.planGroup)
            ) {
                setVoiceModalState(true);
            } else {
                setUpgradePlanModalState(true);
            }
        };

        const handleCloneVoiceOpen = () => {
            mixpanel?.track('clone-voice', { event: 'click' });
            if (isAnonymous) {
                setSignupModalState(true);
            } else if (!user || !user.planGroup || UNSUPPORTED_PLAN_CLONE_VOICE.includes(user.planGroup)) {
                setShowVoiceCloneUpgradeModal(true);
            } else if (seats === 0) {
                toast.info({
                    title: 'Voice cloning limit reached',
                    message: 'Delete an existing voice or upgrade your plan to clone additional voices',
                });
            } else {
                mixpanel?.track('clone-voice', { event: 'view' });
                setCloneVoiceWizardModal(true);
            }
        };
        const showCloneVoiceTab = value === 'default';

        return (
            <StyledList ref={ref} {...rest}>
                <StyledTabs
                    value={value}
                    onChange={handleChange}
                    TabIndicatorProps={{
                        style: { background: theme.colors.secondary.main },
                    }}>
                    <Tab label="Voices" value="default" />
                    <Tab label="Imported voices" value="imported" />
                </StyledTabs>
                {showCloneVoiceTab && (
                    <TopOption>
                        <StyledIconButton ariaLabel="Add voice by API" onClick={handleCloneVoiceOpen}>
                            <MaskedIcon src={CloneVoice} size="20px" />
                            Clone your voice
                            <StyledNew>New</StyledNew>
                        </StyledIconButton>
                    </TopOption>
                )}
                <OptionWrapper isFooter={true}>
                    {isSupportedLanguage(agentEditorStore.ttsSelectedLanguage) || value === 'default' ? (
                        rest.children
                    ) : (
                        <NotSupportedVoiceContainer>
                            <img src={Microphone} />
                            Imported voices are not supported for this language
                        </NotSupportedVoiceContainer>
                    )}
                </OptionWrapper>

                {filter === 'imported' && (
                    <MultiSelectFooter>
                        {rest?.children ? (
                            <StyledIconButton ariaLabel="Manage 3rd party voices" onClick={handleModalOpen}>
                                <MaskedIcon src={Settings} size="24px" />
                                Manage imported voices
                            </StyledIconButton>
                        ) : (
                            <StyledIconButton ariaLabel="Add voice by API" onClick={handleModalOpen}>
                                <MaskedIcon src={Plus} size="20px" color={theme.colors.secondary.main} />
                                Add voice by API
                            </StyledIconButton>
                        )}
                    </MultiSelectFooter>
                )}
            </StyledList>
        );
    })
);
